import React, { useEffect } from "react";
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {
    BrowserRouter as Router,
    Route,
    Routes,
    useLocation,
} from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';

import Home from "./pages/home";
import SetupExplainerPage from "./pages/resource-pages/setupExplainerPage";
import AboutMe from "./pages/aboutMe";
import Shop from "./pages/shop";
import OnsiteSEO from "./pages/resource-pages/onsiteSEO";
import Checkout from "./pages/checkout";
import OrderSummary from "./pages/orderSummary";
import PageNotFound from "./pages/pageNotFound";
import ResearchExplainerPage from "./pages/resource-pages/researchExplainerPage";
import TechnicalSEOExplainerPage from "./pages/resource-pages/technicalSEOExplainerPage";
import PricingRetainer from "./pages/pricingRetainer";
import { sendSlackMessageTracked } from "./models/notifications";
import PrivacyPolicy from "./pages/privacyPolicy";
import ContactUs from "./pages/contactUs";
import GenericPromotionalPage from "./pages/promotional-pages/genericPromotionalPage";
import { currentHolidayLong, isHolidayFocus } from "./models/variableCopywriting";
import GeoLandingPage from "./components/services/geoPage";
import ServicesHubPage from "./pages/hubPages/servicesHubPage";
import CaseStudiesHubPage from "./pages/hubPages/caseStudiesHubPage";
import {ClerkProvider} from "@clerk/react";
import {AuthPage} from "./pages/authPage";
import Dashboard from "./pages/dashboard";
import Onboarding from "./pages/onboarding";
import DashboardHomeComponent from "./components/dashboard/dashboardHomeComponent";

const getPageTitle = (pathname) => {
    if (pathname.endsWith("/")) {
        pathname = pathname.slice(0, -1);
    }
    if (pathname.includes("/offer")) {
        if (isHolidayFocus) {
            return currentHolidayLong;
        }
        return "SEO Offer Page";
    }

    switch (pathname) {
        case "":
            return "Home";
        // Shop
        case "/shop":
            return "Shop SEO Services";

        // Case Studies
        case "/case-studies":
            return "Best SEO Case Studies | HerStory SEO";
        case "/case-studies/home":
            return "Most Successful SEO Case Studies | HerStory SEO";

        // Services
        case "/services":
            return "SEO Services";
        case "/services/home":
            return "All SEO Services | HerStory SEO";
        case "/services/geo":
            return "Generative Engine Optimization";

        case "/login":
            return "HerStory SEO Login";
        case "/signup":
            return "HerStory SEO Signup";
        case "/auth":
            return "HerStory SEO Login";
        case "/auth/login":
            return "HerStory SEO Login";
        case "/auth/signup":
            return "HerStory SEO Signup";
        case "/onboarding":
            return "HerStory SEO Onboarding Flow";

        // Dashboard
        case "/dashboard":
            return "HerStory SEO Dashboard";

        case "/services/setup":
            return "SEO Setup";
        case "/services/onsite-seo":
            return "Onsite SEO";
        case "/services/seo-research":
            return "SEO Research";
        case "/services/technical":
            return "Technical SEO Component";
        case "/seo-retainer":
            return "SEO Retainer";
        case "/checkout":
            return "Checkout";
        case "/orderSummary":
            return "Order Summary";
        case "/about":
            return "About";
        case "/pricing":
            return "Pricing";
        case "/contact-us":
            return "Contact SEO Experts | HerStory";
        case "/privacy-policy":
            return "Privacy Policy";
        // Promotional pages
        case "/black-friday-cyber-monday-application":
            return `${currentHolidayLong} Application | HerStory`;
        case "/black-friday-cyber-monday-sale":
            return `${currentHolidayLong} Sale | HerStory`;
        default:
            return "Not Found";
    }
};

// Component to update page title using Helmet
const PageTitle = () => {
    const location = useLocation();
    const title = getPageTitle(location.pathname);
    return <Helmet><title>{title}</title></Helmet>;
};

// Component to track page views (logging only)
const TrackPageViews = () => {
    const location = useLocation();
    useEffect(() => {
        const fullUrlAfterDomain = location.pathname + location.search;
        sendSlackMessageTracked(`Customer is visiting the ${fullUrlAfterDomain} page`);
    }, [location]);
    return null;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

root.render(
    <React.StrictMode>
        <HelmetProvider>
            <Router>
                <PageTitle />
                <TrackPageViews />
                <ClerkProvider
                    publishableKey={publishableKey}
                    signInFallbackRedirectUrl="/dashboard"
                    signUpFallbackRedirectUrl="/dashboard">
                    <Routes>
                        <Route path="/" rel="Home" element={<Home />} />
                        <Route path="/services" rel="Shop" element={<Shop />} />
                        <Route path="/shop" rel="Shop" element={<Shop />} />
                        <Route path="/services/setup" rel="Issues" element={<SetupExplainerPage />} />
                        <Route path="/services/onsite-seo" rel="Onsite SEO" element={<OnsiteSEO />} />
                        <Route path="/services/seo-research" rel="SEO Research" element={<ResearchExplainerPage />} />
                        <Route path="/services/technical" rel="SEO Technical Component" element={<TechnicalSEOExplainerPage />} />
                        <Route path="/about" rel="About" element={<AboutMe />} />
                        <Route path="/seo-retainer" rel="SEO Retainer" element={<PricingRetainer />} />
                        <Route path="/checkout" rel="Checkout" element={<Checkout />} />
                        <Route path="/orderSummary" rel="Order Summary" element={<OrderSummary />} />

                        {/* Account Pages */}
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/signup" element={<AuthPage />} />
                        <Route path="/auth" element={<AuthPage />} />
                        <Route path="/auth/login" element={<AuthPage isSignIn={true} />} />
                        <Route path="/auth/signup" element={<AuthPage />} />
                        <Route path="/onboarding" element={<Onboarding />} />

                        {/* Dashboard Pages */}
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard2" element={<DashboardHomeComponent />} />

                        {/* Services Pages */}
                        <Route path="/services/home" rel="All SEO Services | HerStory SEO" element={<ServicesHubPage />} />
                        <Route path="/services/geo" rel="Generative Engine Optimization" element={<GeoLandingPage />} />

                        {/* Case Studies Pages */}
                        <Route path="/case-studies" rel="All SEO Case Studies | HerStory SEO" element={<CaseStudiesHubPage />} />
                        <Route path="/case-studies/home" rel="All SEO Case Studies | HerStory SEO" element={<CaseStudiesHubPage />} />


                        {/* Promotional Pages */}
                        <Route path="/black-friday-cyber-monday-sale" rel="Black Friday & Cyber Monday (BFCM) Sale" element={<GenericPromotionalPage />} />
                        {/* Variable Holiday Trigger */}
                        <Route path="/offer/*" rel={currentHolidayLong} element={<GenericPromotionalPage />} />
                        <Route path="/contact-us" rel="Contact SEO Experts | HerStory" element={<ContactUs />} />
                        <Route path="/privacy-policy" rel="Privacy Policy" element={<PrivacyPolicy />} />
                        <Route path="*" rel="404 - Not Found" element={<PageNotFound />} />
                        {/* For future pages added */}
                        {/* <Route path="/" element={<HomePage/>}/>  */}
                    </Routes>
                </ClerkProvider>
            </Router>
        </HelmetProvider>
    </React.StrictMode>
);

reportWebVitals();